import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, TimeoutError, catchError, throwError, timeout } from 'rxjs';

/**
 * Tempo máximo de espera por requisição.
 *
 * Generoso de propósito: é rede lenta, não pico de latência, que precisa ser
 * interrompida. Relatórios e consultas de período longo continuam cabendo.
 */
const LIMITE_DE_ESPERA_MS = 30_000;

/**
 * Mensagem exibida ao usuário. Diz que a operação pode ter sido concluída no
 * servidor, porque numa escrita interrompida por espera esgotada isso é
 * verdade — o cliente desistiu de esperar, o servidor não necessariamente
 * desistiu de gravar.
 */
const MENSAGEM_DE_ESPERA_ESGOTADA =
  'O servidor demorou demais para responder. A operação pode não ter sido concluída: confira antes de tentar de novo.';

/**
 * Interrompe requisições que não respondem.
 *
 * Existe como interceptor, e não em cada chamada, por dois motivos. O
 * primeiro é alcance: sem ele, uma requisição pendurada deixa o overlay
 * global de carregamento de pé para sempre — ele é `position: fixed` sobre a
 * viewport inteira e não tem `pointer-events: none`, então cobre até a
 * toolbar, e só recarregar a página devolve a tela ao usuário. O
 * `LoadingInterceptor` só desempilha no `finalize`, que nunca roda numa
 * resposta que não chega.
 *
 * Chamadas com limite próprio mais estrito (ajuste de estoque, duplicação,
 * origens da ficha) mantêm o seu: este é o teto da casa, não um substituto
 * para quem precisa desistir antes.
 *
 * O segundo motivo é o tratamento: `timeout()` aplicado no pipe de um componente
 * fica a JUSANTE dos interceptors, então o `TimeoutError` nasce depois do
 * `ErrorHandlerInterceptor` e nunca chega nele — o usuário via apenas a tela
 * destravar, sem nenhuma explicação. Aqui, o erro é convertido em
 * `HttpErrorResponse` e sobe pelo tratador global como qualquer outra falha,
 * porque este interceptor é registrado DEPOIS dele (mais perto da rede).
 */
@Injectable()
export class TimeoutInterceptor implements HttpInterceptor {
  intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    return next.handle(request).pipe(
      timeout(LIMITE_DE_ESPERA_MS),
      catchError((erro: unknown) =>
        erro instanceof TimeoutError
          ? throwError(() => esperaEsgotada(request))
          : throwError(() => erro)
      )
    );
  }
}

/**
 * O erro precisa chegar ao tratador global no formato que ele entende. `0`
 * como status é a convenção para falha sem resposta do servidor, a mesma de
 * uma queda de rede.
 */
function esperaEsgotada(request: HttpRequest<unknown>): HttpErrorResponse {
  return new HttpErrorResponse({
    status: 0,
    statusText: 'Tempo de espera esgotado',
    url: request.url,
    error: { userMessage: MENSAGEM_DE_ESPERA_ESGOTADA },
  });
}
